import { useState } from "react";
import { cn } from "@/lib/utils";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  FileText,
  FileSpreadsheet,
  FileDown,
  Printer,
} from "lucide-react";

interface ReportFiltersProps {
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
  show?: string[];
}

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i).map(String);

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const PAYMENT_METHODS = [
  { value: "all", label: "All Methods" },
  { value: "cash", label: "Cash" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
];

const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "contribution", label: "Contribution" },
  { value: "loan", label: "Loan" },
  { value: "repayment", label: "Repayment" },
  { value: "dividend", label: "Dividend" },
  { value: "fine", label: "Fine" },
];

const ALL_FILTER_KEYS = [
  "dateStart",
  "dateEnd",
  "month",
  "year",
  "memberId",
  "status",
  "groupId",
  "type",
  "paymentMethod",
];

export function ReportFilters({ filters, onChange, show }: ReportFiltersProps) {
  const [isOpen, setIsOpen] = useState(true);
  const visibleFilters = show ?? ALL_FILTER_KEYS;

  const updateFilter = (key: string, value: any) => {
    onChange({ ...filters, [key]: value });
  };

  const clearAll = () => {
    const cleared: Record<string, any> = { ...DEFAULT_REPORT_FILTERS };
    visibleFilters.forEach((key) => {
      if (key === "status" || key === "type" || key === "paymentMethod") {
        cleared[key] = "all";
      } else if (!(key in DEFAULT_REPORT_FILTERS)) {
        cleared[key] = "";
      }
    });
    onChange(cleared);
  };

  const hasActiveFilters = visibleFilters.some((key) => {
    const val = filters[key];
    const defaultVal = DEFAULT_REPORT_FILTERS[key];
    if (val === undefined || val === "") return false;
    if (defaultVal !== undefined && val === defaultVal) return false;
    if (val === "all") return false;
    return true;
  });

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          "rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all",
          isOpen && "pb-4"
        )}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between px-5 py-3.5 text-left"
          >
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                <Filter className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              <span className="text-sm font-semibold text-gray-900">Report Filters</span>
              {hasActiveFilters && (
                <span className="inline-flex items-center rounded-full bg-[#009A44]/10 px-2 py-0.5 text-[10px] font-semibold text-[#009A44]">
                  Active
                </span>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-3 px-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleFilters.includes("dateStart") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </label>
                  <Input
                    type="date"
                    value={filters.dateStart || ""}
                    onChange={(e) => updateFilter("dateStart", e.target.value)}
                    className="h-9 border-gray-200 text-sm"
                  />
                </div>
              )}

              {visibleFilters.includes("dateEnd") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </label>
                  <Input
                    type="date"
                    value={filters.dateEnd || ""}
                    onChange={(e) => updateFilter("dateEnd", e.target.value)}
                    className="h-9 border-gray-200 text-sm"
                  />
                </div>
              )}

              {visibleFilters.includes("month") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </label>
                  <Select
                    value={filters.month || ""}
                    onValueChange={(v) => updateFilter("month", v)}
                  >
                    <SelectTrigger className="h-9 border-gray-200 text-sm">
                      <SelectValue placeholder="All months" />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={m.value} className="text-sm">
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {visibleFilters.includes("year") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </label>
                  <Select
                    value={filters.year || ""}
                    onValueChange={(v) => updateFilter("year", v)}
                  >
                    <SelectTrigger className="h-9 border-gray-200 text-sm">
                      <SelectValue placeholder="All years" />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {YEARS.map((y) => (
                        <SelectItem key={y} value={y} className="text-sm">
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {visibleFilters.includes("status") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(v) => updateFilter("status", v)}
                  >
                    <SelectTrigger className="h-9 border-gray-200 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="text-sm">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {visibleFilters.includes("paymentMethod") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Payment Method
                  </label>
                  <Select
                    value={filters.paymentMethod || "all"}
                    onValueChange={(v) => updateFilter("paymentMethod", v)}
                  >
                    <SelectTrigger className="h-9 border-gray-200 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {PAYMENT_METHODS.map((pm) => (
                        <SelectItem key={pm.value} value={pm.value} className="text-sm">
                          {pm.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {visibleFilters.includes("groupId") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </label>
                  <Input
                    type="text"
                    placeholder="Filter by group"
                    value={filters.groupId || ""}
                    onChange={(e) => updateFilter("groupId", e.target.value)}
                    className="h-9 border-gray-200 text-sm"
                  />
                </div>
              )}

              {visibleFilters.includes("type") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </label>
                  <Select
                    value={filters.type || "all"}
                    onValueChange={(v) => updateFilter("type", v)}
                  >
                    <SelectTrigger className="h-9 border-gray-200 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {TYPE_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="text-sm">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {visibleFilters.includes("memberId") && (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </label>
                  <Input
                    type="text"
                    placeholder="Filter by member"
                    value={filters.memberId || ""}
                    onChange={(e) => updateFilter("memberId", e.target.value)}
                    className="h-9 border-gray-200 text-sm"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 pt-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={!hasActiveFilters}
                  className="text-gray-500 hover:text-gray-700 gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange({ ...DEFAULT_REPORT_FILTERS })}
                  className="text-[#009A44] hover:text-[#007a36] hover:bg-[#009A44]/5 gap-1.5"
                >
                  <Filter className="h-3.5 w-3.5" />
                  This Month
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
                >
                  <FileText className="h-3.5 w-3.5" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
